import { useTranslation } from "react-i18next";
import { Button, Checkbox, Form, Icon, Input, Loader, Modal } from "semantic-ui-react";

import { Dropdown, Pagination, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, SEASONS } from "models";
import {
  TyreExternalOfferTable,
  TyreOfferTable
} from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/components";
import { useTyreReplacementOffer } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/hooks";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/ReplacementOfferModal.scss";
import { useAddTyreReplacementMutation, usePermanentTyreDiscountOptions, usePermanentTyreDiscounts } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { replacementOffersToVehicleTyreReplacements } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { parseIntOrDefault } from "utils/common";

import type { Appointment, DealerLocation, VehicleTyre, VehicleTyreReplacement } from "models";
import type { FocusEvent, FormEvent } from "react";
import type { ModalProps } from "semantic-ui-react";

type ReplacementOfferModalProps = ModalProps & {
  appointment: Appointment;
  location: DealerLocation | undefined;
  existingReplacements: VehicleTyreReplacement[];
  tyre: VehicleTyre | null;
  onRequestClose?: () => void;
};

const MAX_RESULTS_PER_PAGE = 50;

export const ReplacementOfferModal = ({ appointment, location, existingReplacements, tyre, onRequestClose }: ReplacementOfferModalProps) => {
  const { t } = useTranslation();

  const offerModal = useTyreReplacementOffer({ location, vehicleTyre: tyre, existingReplacements });
  const { data: tyreDiscountOptions = [] } = usePermanentTyreDiscountOptions(location);
  const { data: tyreDiscounts = [], isFetching: isLoadingDiscounts } = usePermanentTyreDiscounts(location);
  const addReplacements = useAddTyreReplacementMutation();
  const canUpdateAppointments = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Appointments);
  const canSearchTyreTeamCatalog = useCan(ACL_ACTIVITIES.Search, ACL_RESOURCES.TyreTeamCatalog);

  const lastPage = Math.max(1, Math.ceil(offerModal.combinedReplacementOffers.length / MAX_RESULTS_PER_PAGE));
  const visiblePage = Math.min(offerModal.currentPage, lastPage);
  const pageOffset = (visiblePage - 1) * MAX_RESULTS_PER_PAGE;
  const paginatedReplacementOffers = offerModal.combinedReplacementOffers.slice(pageOffset, pageOffset + MAX_RESULTS_PER_PAGE);

  const seasonOptions = [
    { key: SEASONS.AllSeason, value: SEASONS.AllSeason, text: t("v8_all_season", "All season") },
    { key: SEASONS.Summer, value: SEASONS.Summer, text: t("v8_summer", "Summer") },
    { key: SEASONS.Winter, value: SEASONS.Winter, text: t("v8_winter", "Winter") }
  ];

  const handleSave = async () => {
    const selection = offerModal.combinedReplacementOffers.filter(sr => sr.selected);
    if (!selection.length || !tyre) return;

    await addReplacements.mutateAsync(replacementOffersToVehicleTyreReplacements(selection, appointment.id, tyre));

    onRequestClose?.();
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (offerModal.isExternalCatalogEnabled && !canSearchTyreTeamCatalog) return;

    if (document.activeElement instanceof HTMLInputElement) document.activeElement.blur();
    offerModal.handleSearch();
  };

  const handleCancel = () => onRequestClose?.();

  return (
    <Modal className="TyreReplacementModal" closeOnEscape size="fullscreen" open={tyre !== null} onClose={onRequestClose} closeOnDimmerClick>
      <Modal.Header>{t("v8_add_replacement", "Add Replacement")}</Modal.Header>
      <Modal.Content scrolling className="modal-content-container">
        <Form onSubmit={handleSearchSubmit}>
          {offerModal.isExternalCatalogEnabled && (
            <Form.Field width={6} className="TyreReplacementModal__discount-dropdown">
              <label>{t("v8_discount_name", "Discount Name")}</label>
              <Dropdown
                clearable
                options={tyreDiscountOptions}
                value={offerModal.selectedDiscount?.id}
                selectOnBlur={false}
                onChange={selectedDiscount => offerModal.handleSelectDiscount(tyreDiscounts.find(discount => discount.id === selectedDiscount) ?? null)}
                placeholder={t("v8_select_discount", "Select discount")}
                fluid
                selection
              />
            </Form.Field>
          )}

          <Form.Group>
            <Form.Field width={2} required>
              <label>{t("v8_width", "Width")}</label>
              <Input
                key={`${tyre?.id}-width`}
                defaultValue={offerModal.prefilledDimensions.width ?? ""}
                placeholder={t("v8_width", "Width")}
                fluid
                name="width"
                required
                onBlur={(event: FocusEvent<HTMLInputElement>) => offerModal.setWidth(parseIntOrDefault(event.target.value, null))}
              />
            </Form.Field>

            <Form.Field width={2} disabled={!offerModal.width}>
              <label>{t("v8_height", "Height")}</label>
              <Input
                key={`${tyre?.id}-height`}
                defaultValue={offerModal.prefilledDimensions.height ?? ""}
                placeholder={t("v8_height", "Height")}
                fluid
                name="height"
                onBlur={(event: FocusEvent<HTMLInputElement>) => offerModal.setHeight(parseIntOrDefault(event.target.value, null))}
              />
            </Form.Field>

            <Form.Field width={2} disabled={!offerModal.width || !offerModal.height}>
              <label>{t("v8_size", "Size")}</label>
              <Input
                key={`${tyre?.id}-size`}
                defaultValue={offerModal.prefilledDimensions.size ?? ""}
                placeholder={t("v8_size", "Size")}
                fluid
                name="size"
                onBlur={(event: FocusEvent<HTMLInputElement>) => offerModal.setSize(parseIntOrDefault(event.target.value, null))}
              />
            </Form.Field>

            <Form.Field width={4}>
              <label>{t("v8_brand", "Brand")}</label>
              <Dropdown
                selection
                selectOnBlur={false}
                loading={offerModal.isFetchingBrands}
                options={offerModal.tyreBrandOptions}
                clearable
                className="DeliveryDropdown"
                search
                value={offerModal.manufacturer}
                onChange={value => offerModal.setManufacturer(value ?? "")}
                name="manufacturer"
              />
            </Form.Field>

            <Form.Field width={3}>
              <label>{t("v8_season", "Season")}</label>
              <Dropdown
                selection
                className="DeliveryDropdown"
                selectOnBlur={false}
                clearable
                options={seasonOptions}
                value={offerModal.season ?? undefined}
                onChange={season => offerModal.setSeason(season ?? null)}
                name="season"
              />
            </Form.Field>

            {offerModal.isExternalCatalogEnabled && (
              <>
                <Form.Field width={6} required>
                  <label>{t("v8_delivery", "Delivery")}</label>
                  <Dropdown
                    selection
                    selectOnBlur={false}
                    clearable
                    className="DeliveryDropdown"
                    value={offerModal.selectedDeliveryIds}
                    options={offerModal.getDeliveriesOptions()}
                    placeholder={t("v8_select_deliveries", "Select deliveries")}
                    loading={offerModal.isLoadingTyreDeliveries}
                    onChange={offerModal.setSelectedDeliveryIds}
                    name="delivery"
                    multiple
                  />
                </Form.Field>

                <Form.Field width={1} className="in-stock-form-field">
                  <label className="no-wrap">{t("v8_in_stock", "In Stock")}</label>
                  <Checkbox
                    toggle
                    className="in-stock-checkbox"
                    defaultChecked={offerModal.inStock}
                    name="in_stock"
                    onChange={(_e, data) => offerModal.setInStock(data.checked ?? false)}
                  />
                </Form.Field>
              </>
            )}

            <Form.Field className="no-padding-right">
              <label className="search-form-label">{t("v8_search", "Search")}</label>
              <Button
                type="submit"
                className="search-button"
                disabled={!offerModal.selectedDeliveryIds.length || offerModal.isSearching || offerModal.isFetchingBrands || isLoadingDiscounts || !offerModal.width}
                loading={offerModal.isSearching || offerModal.isFetchingBrands || isLoadingDiscounts}
              >
                {t("v8_search", "Search")}
              </Button>
            </Form.Field>
          </Form.Group>
        </Form>

        {offerModal.isSearching && (
          <div className="tyre-team-list-loading">
            <Loader active inline size="large" className="Loader">
              {t("v8_loading_tyres", "Loading tyres")}...
            </Loader>
          </div>
        )}

        {!offerModal.isSearching && offerModal.combinedReplacementOffers.length === 0 && (
          <div className="no-results-container">
            <p>{t("v8_no_tyres_found", "No tyres found")}</p>
          </div>
        )}

        {!offerModal.isSearching &&
          offerModal.combinedReplacementOffers.length > 0 &&
          (offerModal.isExternalCatalogEnabled ? (
            <TyreExternalOfferTable
              replacementOffers={paginatedReplacementOffers}
              is_tyre_net_enabled={location?.is_tyre_net_enabled}
              getDisplayedOfferPrice={offerModal.getDisplayedOfferPrice}
              onSelectionUpdate={offerModal.handleSelectionUpdate}
              getDeliveryChannelName={offerModal.getDeliveryChannelName}
              getDeliveryPopup={offerModal.getDeliveryPopup}
            />
          ) : (
            <TyreOfferTable
              replacementOffers={paginatedReplacementOffers}
              appointment={appointment}
              getDisplayedOfferPrice={offerModal.getDisplayedOfferPrice}
              onSelectionUpdate={offerModal.handleSelectionUpdate}
              onPriceUpdate={offerModal.handlePriceUpdate}
              canUpdateAppointments={canUpdateAppointments}
            />
          ))}

        {!offerModal.isSearching && offerModal.combinedReplacementOffers.length > MAX_RESULTS_PER_PAGE && (
          <div className="pagination-container">
            <Pagination
              currentPage={visiblePage}
              pageSize={MAX_RESULTS_PER_PAGE}
              totalPages={lastPage}
              totalItems={offerModal.combinedReplacementOffers.length}
              onPageChange={offerModal.setCurrentPage}
            />
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <div className="action-buttons">
          <Button className="light" onClick={handleCancel} type="button">
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button color="green" type="button" onClick={handleSave} disabled={offerModal.isSearching || addReplacements.isPending} loading={addReplacements.isPending}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
